import React from "react";
import { Button } from "../components/ui/button";
import Lantern from "../components/Lantern";
import Purse from "../components/Purse";
import { Download } from "lucide-react";

const LANTERN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#0B3B24" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" width="120" height="120"><path d="M12 2v2"/><path d="M7 5h10"/><path d="M8 6c-1 1.5-1.5 3.2-1.5 5.5C6.5 15 8.5 17.5 12 17.5s5.5-2.5 5.5-6c0-2.3-.5-4-1.5-5.5Z"/><path d="M9 18.5h6"/><path d="M10 20.5h4"/><path d="M12 9.5c-1 1.2-1.5 2.2-1.5 3.2 0 1 .7 1.8 1.5 1.8s1.5-.8 1.5-1.8c0-1-.5-2-1.5-3.2Z" fill="#FBBF24" stroke="#FBBF24"/></svg>`;

const PURSE_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="120" height="120"><path d="M22 16c0-4 4-8 10-8s10 4 10 8" stroke="#0B3B24" stroke-width="2.5" stroke-linecap="round" fill="none"/><path d="M14 22c2-3 6-4 18-4s16 1 18 4l4 28c.6 4.2-2.6 8-7 8H17c-4.4 0-7.6-3.8-7-8l4-28Z" fill="#0B3B24" stroke="#0B3B24" stroke-width="1.5" stroke-linejoin="round"/><path d="M14 22c2-3 6-4 18-4s16 1 18 4" stroke="#FBBF24" stroke-width="3" stroke-linecap="round" fill="none"/><circle cx="32" cy="38" r="6" fill="#FBBF24"/></svg>`;

const download = (filename, content, mime = "image/svg+xml") => {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const PALETTE = [
  { name: "Primary Green",  hex: "#0B3B24", desc: "Trust, growth, African forests" },
  { name: "Terracotta",     hex: "#C65D47", desc: "Warmth, family, accent CTAs" },
  { name: "Gold Flame",     hex: "#FBBF24", desc: "Light, value, success moments" },
  { name: "Sand Background",hex: "#FDFBF7", desc: "Calm, paper-like base" },
  { name: "Ink",            hex: "#1A1F1C", desc: "Body text" },
  { name: "Muted",          hex: "#5C6A61", desc: "Secondary copy" },
];

const Swatch = ({ name, hex, desc }) => (
  <div className="ra-card p-4" data-testid={`palette-${name.replace(/\s/g, "-").toLowerCase()}`}>
    <div className="h-20 rounded-md mb-3 border border-[var(--ra-border)]" style={{ background: hex }} />
    <div className="font-semibold">{name}</div>
    <div className="text-xs font-mono text-[var(--ra-muted)]">{hex}</div>
    <div className="text-xs text-[var(--ra-muted)] mt-1">{desc}</div>
  </div>
);

const Brand = () => (
  <div className="max-w-6xl mx-auto px-5 sm:px-8 py-12" data-testid="brand-root">
    <div className="fade-up mb-12">
      <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-accent)] font-semibold mb-2">Brand kit · v1</div>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight">ClearPurse Money</h1>
      <p className="text-[var(--ra-muted)] text-lg max-w-2xl mt-3">
        A fintech remittance brand built around <strong>clarity, warmth, and family</strong>.
        Two icons tell the story: a <strong>purse</strong> that seals safely when you send,
        and a <strong>lantern</strong> that glows when your money arrives home — clear rates,
        clear fees, a clear way home for every dollar.
      </p>
    </div>

    {/* Logos */}
    <section className="mb-14 fade-up fade-up-2">
      <h2 className="font-heading text-2xl font-bold mb-1">Logo marks</h2>
      <p className="text-sm text-[var(--ra-muted)] mb-6">Primary brand assets. Use on light backgrounds.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="ra-card p-8 flex flex-col items-center" data-testid="brand-lantern-card">
          <div className="w-32 h-32 rounded-2xl bg-[var(--ra-primary)] flex items-center justify-center">
            <Lantern size={80} className="text-white" glow />
          </div>
          <div className="font-heading text-lg font-semibold mt-5">Lantern · arrival</div>
          <p className="text-xs text-[var(--ra-muted)] text-center mt-1 mb-4 max-w-xs">Used as primary app icon, in nav, and on the success moment.</p>
          <Button onClick={() => download("clearpurse-lantern.svg", LANTERN_SVG)} variant="outline" size="sm" className="rounded-md" data-testid="dl-lantern">
            <Download size={14} className="mr-2" /> Download SVG
          </Button>
        </div>
        <div className="ra-card p-8 flex flex-col items-center" data-testid="brand-purse-card">
          <Purse size={130} />
          <div className="font-heading text-lg font-semibold mt-5">Purse · sending</div>
          <p className="text-xs text-[var(--ra-muted)] text-center mt-1 mb-4 max-w-xs">Used in the pay step and during in-transit micro-animations.</p>
          <Button onClick={() => download("clearpurse-purse.svg", PURSE_SVG)} variant="outline" size="sm" className="rounded-md" data-testid="dl-purse">
            <Download size={14} className="mr-2" /> Download SVG
          </Button>
        </div>
      </div>
    </section>

    {/* Color palette */}
    <section className="mb-14 fade-up fade-up-3">
      <h2 className="font-heading text-2xl font-bold mb-1">Color palette</h2>
      <p className="text-sm text-[var(--ra-muted)] mb-6">Earthy, warm, trust-forward. Avoid neon, avoid pure black.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {PALETTE.map(c => <Swatch key={c.hex} {...c} />)}
      </div>
    </section>

    {/* Typography */}
    <section className="mb-14 fade-up fade-up-4">
      <h2 className="font-heading text-2xl font-bold mb-1">Typography</h2>
      <p className="text-sm text-[var(--ra-muted)] mb-6">Distinctive yet readable. Outfit for personality, Manrope for body comfort.</p>
      <div className="grid md:grid-cols-2 gap-6">
        <div className="ra-card p-6">
          <div className="text-xs uppercase tracking-wider text-[var(--ra-muted)] mb-2">Headings · Outfit</div>
          <div className="font-heading text-5xl font-bold leading-none">Send money home.</div>
          <div className="text-xs font-mono text-[var(--ra-muted)] mt-4">font-family: Outfit · 700</div>
        </div>
        <div className="ra-card p-6">
          <div className="text-xs uppercase tracking-wider text-[var(--ra-muted)] mb-2">Body · Manrope</div>
          <p className="text-base leading-relaxed">Zero fees and a strong exchange rate, so more of every dollar reaches your family — a clear way home.</p>
          <div className="text-xs font-mono text-[var(--ra-muted)] mt-4">font-family: Manrope · 400/500</div>
        </div>
      </div>
    </section>

    {/* Voice */}
    <section className="mb-12 fade-up">
      <h2 className="font-heading text-2xl font-bold mb-1">Brand voice</h2>
      <p className="text-sm text-[var(--ra-muted)] mb-6">Three tones, one personality.</p>
      <div className="grid md:grid-cols-3 gap-5">
        {[
          { title: "Warm, not sugary", body: "Talk to senders like a trusted cousin — confident, calm, never overly cheerful." },
          { title: "Transparent, not technical", body: "Lead with what they receive in real money, not interbank acronyms." },
          { title: "Hopeful, not heroic", body: "We're the quiet middle — the bridge, not the celebrity. The family is the hero." },
        ].map(t => (
          <div key={t.title} className="ra-card p-5">
            <div className="font-heading font-semibold">{t.title}</div>
            <p className="text-sm text-[var(--ra-muted)] mt-1.5 leading-relaxed">{t.body}</p>
          </div>
        ))}
      </div>
    </section>

    <div className="ra-card p-6 bg-[var(--ra-primary)] text-white">
      <div className="font-heading text-lg font-semibold">One-line summary</div>
      <p className="mt-1 text-white/85">ClearPurse Money is the warm, transparent way for the African diaspora to send money home — sealed in a purse, delivered by light.</p>
    </div>
  </div>
);

export default Brand;
